import {
  Accordion,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
  AccordionSummaryProps,
  styled,
} from '@mui/material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { Add, Delete, ExpandMore } from '@mui/icons-material';
import { CirculationAreaForm, CivicItemVM } from '..';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormikProps } from 'formik';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import debounce from 'shell/utils/debounce';
import { Civic } from 'shell/models/civic';
import CivicForm from './civic';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { SystemAuthorizations } from 'shell/authorizations/system';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';
import NewCivicModal from './new-civic-modal';
import { DateTime } from 'luxon';

const StyledAccordionSummary = styled((props: AccordionSummaryProps) => (
  <AccordionSummary {...props} />
))(() => ({
  '& .MuiAccordionSummary-content': {
    margin: '2px 0',
  },
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    padding: '4px 4px 4px 10px',
  },
  '& .MuiInput-root .MuiInput-input': {
    minWidth: '180px',
  },
});

interface FormStreetProps {
  title: string;
  disabled?: boolean;
  circulationAreaForm: FormikProps<CirculationAreaForm>;
  circulationArea?: number;
}

const FormStreet = ({
  title,
  disabled = false,
  circulationAreaForm,
  circulationArea,
}: FormStreetProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);
  const [openNewCivicModal, setOpenNewCivicModal] = useState<boolean>(false);

  const {
    get: getCivicList,
    data: civicM,
    error: civicError,
    loading: loadingCivics,
  } = useHttpFetch<HttpResponseList<Civic>>(
    `${__TOPONYMY_API_URL__}/areaCircolazione/${circulationArea}`
  );

  const { put: deleteCivic } = useHttpFetch<HttpResponse<Civic>>(
    `${__TOPONYMY_API_URL__}/areaCircolazione`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const handleCivicDelete = useCallback(() => {
    deleteCivic(`/civico/cessazione`, {
      motivoCessazione: circulationAreaForm.values.endReason,
      key: {
        dataIstituzione: circulationAreaForm.values.dateKey,
        esponente: circulationAreaForm.values.key,
        codice: circulationArea,
        numero: circulationAreaForm.values.streetNumber?.id || 0,
      },
    }).then(() => {
      circulationAreaForm.setValues({
        ...circulationAreaForm.values,
        electoralSection: '',
        endReason: '',
        civicState: '',
        municipality: '',
        entered: false,
        estabilishmentReason: '',
        temporary: false,
        streetEnd: false,
        cap: '',
        streetNumber: undefined,
        civic: '',
        civicType: '',
        urbanArea: '',
        addressSection: '',
        territorialArea: '',
        municipalMessenger: null,
        censusSection: '',
        localIdentity: '',
        cadastralList: [],
      });
    });
  }, [deleteCivic, circulationAreaForm.setValues, circulationAreaForm.values]);

  const civics: Array<CivicItemVM> = useMemo(() => {
    if (!!civicError || !civicM?.payload) {
      return [];
    }
    return civicM.payload.list.map((civic) => ({
      value: `${civic.key.numero.toString()}${transformExponent(civic.key.esponente)}${
        civic?.dataCessazione
          ? ` Cessato ${DateTime.fromISO(civic?.dataCessazione).toFormat('dd/MM/yyyy')}`
          : ''
      }`,
      id: civic.key.numero,
      esponente: civic.key.esponente,
      dataIstituzione: civic.key.dataIstituzione
        ? DateTime.fromISO(civic?.key.dataIstituzione).toFormat('yyyy-MM-dd')
        : undefined,
    }));
  }, [civicM, civicError]);

  const handleChange = useCallback(
    (value: CivicItemVM) => {
      circulationAreaForm.setFieldValue('streetNumber', value);
    },
    [getCivicList]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const [numero, esponente] = value.split('/') || [];
        getCivicList(`/civico?numeroCivico=${numero}${esponente ? `&esponente=${esponente}` : ''}`);
      },
      [getCivicList]
    )
  );

  const actions = useActionAuthorization({
    detail: [SystemAuthorizations.CivicoQuery],
    delete: [SystemAuthorizations.CivicoDelete],
    edit: [SystemAuthorizations.CivicoUpdate],
  });

  const disabledDelete = useMemo(() => {
    if (disabled) {
      return true;
    }

    if (!actions.delete) {
      return true;
    }

    return !circulationAreaForm.values.streetNumber;
  }, [actions.delete, disabled, circulationAreaForm.values.streetNumber]);

  const disabledForm = useMemo(() => {
    let disabledTemp = false;

    if (!actions.detail && !actions.edit) {
      disabledTemp = true;
    }

    if (!actions.detail && actions.edit) {
      disabledTemp = false;
    }

    return disabledTemp;
  }, [actions]);

  useEffect(() => {
    if (circulationAreaForm.values.streetNumber) return;
    circulationAreaForm.setFieldValue('cadastralList', []);
  }, [circulationAreaForm.values.streetNumber]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        if (circulationAreaForm.values.streetNumber) {
          setOpen(!open);
        }
      }}
    >
      <StyledAccordionSummary
        aria-controls="panel-area-content"
        id="panel-area-header"
        classes={{
          root: 'pmi-flex-row-reverse',
          contentGutters: 'pmi-m-0',
        }}
        expandIcon={
          !circulationAreaForm.values.streetNumber || disabledForm ? undefined : <ExpandMore />
        }
      >
        <div className="pmi-d-flex pmi-align-items-center">
          <Typography>{title}</Typography>
          <div className="pmi-px-3 pmi-my-2">
            <Autocomplete
              options={civics}
              noOptionsText="Nessun elemento"
              loadingText="Caricamento..."
              getOptionLabel={(option: CivicItemVM) => option.value}
              id="streetNumber"
              value={circulationAreaForm.values.streetNumber || null}
              onChange={(event, element) => {
                event.stopPropagation();
                handleChange(element as CivicItemVM);
              }}
              onInputChange={(event, value, reason) => {
                if (!value) {
                  return;
                }
                if (reason === 'input') {
                  handleSearch(value);
                }
              }}
              clearOnBlur={false}
              loading={loadingCivics}
              disabled={disabledForm}
              renderInput={(params) => <StyledTextField {...params} variant="standard" fullWidth />}
            />
          </div>
          <IconButton
            edge="start"
            color="primary"
            aria-label="menu"
            about="auto"
            disabled={disabledForm}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setOpenNewCivicModal(true);
            }}
          >
            <Add />
          </IconButton>
          {openNewCivicModal ? (
            <NewCivicModal
              circulationArea={circulationArea || 0}
              open={openNewCivicModal}
              onClose={() => setOpenNewCivicModal(false)}
              onNo={() => setOpenNewCivicModal(false)}
              onYes={(response) => {
                setOpenNewCivicModal(false);
                handleChange({ id: response || '', value: response?.toString() || '' });
              }}
            />
          ) : undefined}
          <IconButton
            edge="start"
            color="cdmBrand"
            aria-label="menu"
            about="auto"
            disabled={disabledDelete}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleCivicDelete();
            }}
          >
            <Delete />
          </IconButton>
        </div>
      </StyledAccordionSummary>
      {!circulationAreaForm.values.streetNumber || disabledForm ? undefined : (
        <CivicForm
          circulationAreaForm={circulationAreaForm}
          circulationArea={circulationArea}
          disabled={disabled || !actions.edit}
          setOpen={setOpen}
        />
      )}
    </Accordion>
  );
};

FormStreet.displayName = 'Form Street component';

export default FormStreet;
